'use client';

import { useEffect } from 'react';

export default function TranslateCrashGuard() {
  useEffect(() => {
    installTranslateCrashGuard();
  });

  // 這個元件不需要渲染任何實際的 HTML
  return null;
}

declare global {
  interface Window {
    __translateCrashGuardInstalled: boolean;
  }
}

// https://dev.to/nirazanbasnet/why-google-translate-crashes-your-react-app-and-how-to-fix-it-391
function installTranslateCrashGuard() {
  // Server-safe: do nothing during SSR / if the DOM isn't available.
  if (typeof window === 'undefined' || typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  // Install exactly once, even across hot reloads / double invocation.
  if (window.__translateCrashGuardInstalled) return;
  window.__translateCrashGuardInstalled = true;

  // 輔助函式：跨瀏覽器尋找偷天換日的「替身節點」
  const findImposterNode = (parent: Node, child: Node): Node | null => {
    // 情況 1 (Chrome / Edge / Google 翻譯)：尋找塞入的 <font> 標籤
    const fontNode = (parent as Element).querySelector?.('font');
    if (fontNode) return fontNode;

    // 情況 2 (Safari 內建翻譯)：尋找被 Safari 塞進來的新 Text Node
    if (child.nodeType === Node.TEXT_NODE) {
      // 遍歷 parent 的直接子節點，找出留在裡面的 Text Node
      for (const node of Array.from(parent.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          // 這個節點一定不是 child (因為 child 早就脫鉤不在 parent 裡了)
          // 它就是 Safari 留下的翻譯文字！
          return node;
        }
      }
    }

    return null;
  };

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T) {
    if (child.parentNode !== this) {
      // 情況 A：節點仍在樹上
      // A translator already moved this node out from under `this`.
      // React's intent was to delete it, so remove it from wherever it
      // actually lives now — but never throw.
      if (child.parentNode) child.parentNode.removeChild(child);

      // 情況B：節點已被徹底拔除，但翻譯功能留了替身在裡面！（Chrome 的 <font> 或 Safari 的新 Text Node）
      const imposter = findImposterNode(this, child);
      if (imposter && imposter.parentNode === this) {
        // 把瀏覽器偷塞的替身拔除！
        return originalRemoveChild.call(this, imposter) as T;
      }

      // 情況 Y：節點已被徹底拔除，安全略過不噴錯
      return child;
    }

    // 情況 Z：正常的 React 節點，正常刪除
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // 情況 A：如果 referenceNode 被抽換脫鉤了，對齊到 Chrome 的 <font> 或 Safari 的 Text Node 前面
      const imposter = findImposterNode(this, referenceNode);
      if (imposter && imposter.parentNode === this) {
        return originalInsertBefore.call(this, newNode, imposter) as T;
      }

      // 情況 Y：fallback
      // The reference node was re-parented by a translator. Appending keeps
      // the new node visible in the right container instead of throwing.
      return originalInsertBefore.call(this, newNode, null) as T;
    }

    // 情況 Z：正常的 React 插入
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
